import type { PageField } from '~/pages/AskRichardUnified/page/pageContract';

import { SELECTION_FILTERS, type Selections } from './filterSelections';
import { DEFAULT_FILTERS, type LensFilters, VIEWS, type ViewId, previousWindow } from './liveDataAdapter';

const SCHEMA: Record<string, PageField> = {
  view: { type: 'enum', values: [...VIEWS] },
  cp_start: { type: 'text', maxLength: 10 },
  cp_end: { type: 'text', maxLength: 10 },
  pp_start: { type: 'text', maxLength: 10 },
  pp_end: { type: 'text', maxLength: 10 },
  comparison: { type: 'enum', values: ['Custom', 'Last Year'] },
  conv_scope: { type: 'enum', values: ['CP only', 'CP and PP'] },
  margin_max: { type: 'enum', values: ['Exclude', 'Include'] },
  rate_position: { type: 'enum', values: ['All', 'Lead Room', 'Lead Rate', 'Non-Lead Rate'] },
  conv_split1: { type: 'text', maxLength: 80 },
  conv_split2: { type: 'text', maxLength: 80 },
  sup_split1: { type: 'text', maxLength: 80 },
  sup_split2: { type: 'text', maxLength: 80 },
  lpc_split1: { type: 'text', maxLength: 80 },
  lpc_split2: { type: 'text', maxLength: 80 },
};

const PERIOD_FIELDS = ['cp_start', 'cp_end', 'pp_start', 'pp_end', 'comparison', 'conv_scope'] as const;

function reportingPeriodScope(filters: LensFilters): string {
  const prior = previousWindow(filters);
  const savedSnapshot = (['cp_start', 'cp_end', 'pp_start', 'pp_end', 'comparison'] as const).every(
    (key) => filters[key] === DEFAULT_FILTERS[key],
  );
  const label = savedSnapshot ? 'Default reporting period' : 'Selected reporting period';
  return `${label} ${filters.cp_start}–${filters.cp_end}; comparison period ${prior.from}–${prior.to} (${filters.comparison}). The default end date is yesterday; the other default dates are fixed.`;
}

/** The snapshot describes only the selected sheet; dormant controls must not imply a data scope. */
export function accommodationPageContext(view: ViewId, filters: LensFilters, selections: Selections = {}) {
  const tactical = view === VIEWS[3];
  const keys: Array<keyof LensFilters> = tactical
    ? []
    : [
        ...PERIOD_FIELDS,
        ...(view === VIEWS[0]
          ? (['margin_max', 'rate_position', 'conv_split1', 'conv_split2'] as const)
          : view === VIEWS[1]
            ? (['margin_max', 'sup_split1', 'sup_split2'] as const)
            : (['lpc_split1', 'lpc_split2'] as const)),
      ];
  const prior = previousWindow(filters);
  const activeFilters: Record<string, string> = { view };
  const filterSchema: Record<string, PageField> = { view: SCHEMA.view };
  keys.forEach((key) => {
    activeFilters[key] = key === 'pp_start' ? prior.from : key === 'pp_end' ? prior.to : filters[key];
    if (filters.comparison !== 'Last Year' || (key !== 'pp_start' && key !== 'pp_end')) filterSchema[key] = SCHEMA[key];
  });
  // Quick-filter selections are read-only for chat: values such as campaign names contain commas,
  // which the comma-separated edit format cannot carry safely.
  const quickFilters = Object.fromEntries(
    SELECTION_FILTERS[view].filter(({ id }) => selections[id]?.length).map(({ id, label }) => [label, selections[id]]),
  );
  return {
    title: `Accommodation Views, Impressions and Sales · ${view}`,
    filters: activeFilters,
    filterSchema,
    quickFilters,
    dataScope: tactical
      ? 'Historical tactical campaigns, using each campaign’s own during and prior windows. Current/previous period, margin and rate filters do not apply.'
      : reportingPeriodScope(filters),
  };
}
